import { useEffect, useState } from "react";
import { BsWhatsapp } from "react-icons/bs";
import {
  FiCheckCircle,
  FiDownload,
  FiMapPin,
  FiPhone,
  FiSearch,
  FiTrendingUp,
  FiUserPlus,
  FiUsers,
} from "react-icons/fi";
import { IoChevronDown } from "react-icons/io5";
import { MdEmail } from "react-icons/md";
import SuperAdminPremiumSubscriptionCard from "../../components/super-admin/SuperAdminPremiumSubscriptionCard";
import api from "../../configs/api";
import { Z_INDEX } from "../../utils/constants";
import { formatPrice, timeAgo } from "../../utils/helpers";
import { showError, showSuccess } from "../../utils/toastUtils";

const statsData = [
  {
    id: 1,
    title: "No. of Leads",
    value: "—",
    link: "View all Leads",
    percentage: "0.29%",
    isPositive: true,
    icon: FiUserPlus,   
    iconBg: "#735DFF",
  },
  {
    id: 2,
    title: "No. of Deals ",
    value: "—",
    link: "complete revenue",
    percentage: "3.45%",
    isPositive: false,
    icon: FiUsers,
    iconBg: "#FF5A29",
  },
  {
    id: 3,
    title: "Active Deals",
    value: "—",
    link: "Total page views",
    percentage: "11.54%",
    isPositive: true,
    icon: FiTrendingUp,
    iconBg: "#0CC763",
  },
  {
    id: 4,
    title: "Deal Closed",
    value: "—",
    link: "Total profit earned",
    percentage: "0.18%",
    isPositive: true,
    icon: FiCheckCircle,
    iconBg: "#0C9CFC",
  },
];

const StatCard = ({
  title,
  value,
  link,
  percentage,
  isPositive,
  icon: Icon,
  iconBg,
}) => (
  <div className="bg-background-card rounded-lg p-4 sm:p-5 shadow-sm">
    <div className="flex justify-between items-start mb-4">
      <div>
        <p className="text-text-primary font-medium text-sm mb-2">{title}</p>
        <h3 className="text-2xl font-semibold text-text-primary">{value}</h3>
      </div>
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center"
        style={{ backgroundColor: iconBg }}
      >
        <Icon className="text-white text-xl size-7" />
      </div>
    </div>
    <div className="flex justify-between items-center">
      <button className="text-text-muted capitalize leading-relaxed underline underline-offset-2  text-sm">
        {link}
      </button>
      <span
        className={`text-sm font-semibold ${
          isPositive ? "text-status-success" : "text-status-danger"
        }`}
      >
        {percentage}
      </span>
    </div>
  </div>
);

const SearchFilterBar = ({
  searchTerm,
  setSearchTerm,
  selectedMonth,
  setSelectedMonth,
  onExport,
}) => {
  const months = [
    { label: "All Months", value: "All" },
    { label: "January", value: "January" },
    { label: "February", value: "February" },
    { label: "March", value: "March" },
    { label: "April", value: "April" },
    { label: "May", value: "May" },
    { label: "June", value: "June" },
    { label: "July", value: "July" },
    { label: "August", value: "August" },
    { label: "September", value: "September" },
    { label: "October", value: "October" },
    { label: "November", value: "November" },
    { label: "December", value: "December" },
  ];

  return (
    <div
      style={{ zIndex: Z_INDEX.sticky }}
      className="sticky top-0 py-4 bg-background"
    >
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 relative min-w-0">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search by name, phone, email, location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary"
          />
        </div>

        <div className="flex gap-3">
          <div className="relative flex-1 sm:flex-initial">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="appearance-none w-full pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary cursor-pointer sm:min-w-[120px]"
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          </div>

          <button
            onClick={onExport}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <FiDownload className="text-text-secondary" />
            <span className="text-sm font-medium text-text-secondary">
              Export
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterChipsBar = ({
  selectedMonth,
  setSelectedMonth,
  selectedFilter,
  setSelectedFilter,
  searchTerm,
  setSearchTerm,
}) => {
  const hasActive =
    selectedMonth !== "All" ||
    selectedFilter !== "All" ||
    searchTerm.trim() !== "";
  if (!hasActive) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 bg-white shadow-sm border border-gray-200 rounded-md px-4 py-2 mb-4 animate-fadeIn">
      {searchTerm.trim() !== "" && (
        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
          <span>Search: {searchTerm}</span>
          <button
            onClick={() => setSearchTerm("")}
            className="text-gray-500 hover:text-gray-800 transition-colors"
          >
            ✕
          </button>
        </div>
      )}

      {selectedMonth !== "All" && (
        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
          <span>Month: {selectedMonth}</span>
          <button
            onClick={() => setSelectedMonth("All")}
            className="text-gray-500 hover:text-gray-800 transition-colors"
          >
            ✕
          </button>
        </div>
      )}

      {selectedFilter !== "All" && (
        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
          <span>Status: {selectedFilter}</span>
          <button
            onClick={() => setSelectedFilter("All")}
            className="text-gray-500 hover:text-gray-800 transition-colors"
          >
            ✕
          </button>
        </div>
      )}

      <button
        onClick={() => {
          setSearchTerm("");
          setSelectedMonth("All");
          setSelectedFilter("All");
        }}
        className="ml-auto text-sm font-medium text-primary hover:underline"
      >
        Clear all
      </button>
    </div>
  );
};

const LeadCardSkeleton = () => (
  <div className="bg-white rounded-lg font-poppins shadow-sm border-2 border-gray-200 animate-pulse">
    <div className="flex gap-4 p-4">
      <div className="w-20 h-20 rounded-full bg-gray-200" />
      <div className="flex-1 space-y-3">
        <div className="h-4 bg-gray-200 w-1/3 rounded" />
        <div className="h-3 bg-gray-200 w-2/3 rounded" />
        <div className="h-3 bg-gray-200 w-1/2 rounded" />
      </div>
    </div>
  </div>
);

const NoLeadsScreen = ({ isFiltered = false, onReset }) => (
  <div className="bg-white rounded-lg p-8 text-center border border-gray-200 shadow-sm">
    <h3 className="text-lg font-semibold text-text-primary mb-2">
      {isFiltered ? "No leads match your filters" : "No leads yet"}
    </h3>
    <p className="text-text-muted text-sm mb-4">
      {isFiltered
        ? "Try clearing filters to see all leads."
        : "You don't have any leads yet."}
    </p>
    {isFiltered ? (
      <button
        onClick={onReset}
        className="px-4 py-2 rounded-lg bg-primary text-white text-sm"
      >
        Clear filters
      </button>
    ) : null}
  </div>
);

const LeadCard = ({ lead }) => {
  const formattedLead = {
    name: lead?.leadName || "N/A",
    userId: lead?.userId,
    phone: lead?.phoneNumber || "No phone",
    email: lead?.email || "No email",
    location: lead?.location || "Not Provided",
    leadStatus: lead?.leadStatus,
    searchFor: lead?.searchCriteria?.searchFor || "N/A",
    propertyLocation: lead?.searchCriteria?.propertyLocation || "N/A",
    budget:
      lead?.searchCriteria?.budget?.min && lead?.searchCriteria?.budget?.max
        ? `${formatPrice(lead.searchCriteria.budget.min)} - ${formatPrice(
            lead.searchCriteria.budget.max
          )}`
        : "N/A",
    property: {
      _id: lead?.propertyDetails?._id,
      name: lead?.propertyDetails?.name || "N/A",
      developer: lead?.propertyDetails?.builderOrOwner || "N/A",
      image: lead?.propertyDetails?.thumbnail || null,
    },
    agent: {
      _id: lead?.agent?._id || null,
      name: lead?.agent?.name || "N/A",
      email: lead?.agent?.email || "N/A",
      phone: lead?.agent?.phone || "N/A",
    },
    date: timeAgo(lead?.createdAt),
  };

  return (
    <div className="bg-white rounded-lg font-poppins shadow-sm border-2 border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          {/* Profile & Actions */}
          <div className="flex-shrink-0 flex flex-col items-center p-4 sm:p-3">
            <div className="w-20 h-20 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-white font-bold text-xl">
              {formattedLead.name.charAt(0)}
            </div>
            <div className="flex sm:flex-col gap-2 mt-4">
              <button className="p-2.5 flex items-center justify-center size-11 rounded-full bg-status-success/10 hover:bg-status-success/20 transition-colors">
                <BsWhatsapp className="text-status-success w-5 h-5 sm:w-full sm:h-full" />
              </button>
              <button className="p-2.5 flex items-center justify-center size-11 rounded-full bg-status-info/10 hover:bg-status-info/20 transition-colors">
                <MdEmail className="text-status-info w-5 h-5 sm:w-full sm:h-full" />
              </button>
              <button className="p-2.5 flex items-center justify-center size-11 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors">
                <FiPhone className="text-primary w-5 h-5 sm:w-full sm:h-full" />
              </button>
            </div>
          </div>

          {/* Details */}
          <div className="flex-1 flex-col divide-y divide-gray-200">
            {/* User Details & Status */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:border-r py-4 px-4 lg:px-0 border-primary-navy/10">
                <h2 className="font-medium text-base sm:text-sm lg:text-base">
                  {formattedLead.name}
                </h2>
                <p className="text-sm mt-2 text-text-secondary">
                  {formattedLead.userId}
                </p>
              </div>
              <div className="space-y-2  py-4 px-4 lg:px-0 text-sm">
                <div className="flex items-center gap-2 text-text-secondary">
                  <FiPhone className="flex-shrink-0" />
                  <span className="break-all sm:break-normal">
                    {formattedLead.phone}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-text-secondary">
                  <MdEmail className="flex-shrink-0" />
                  <span className="truncate">{formattedLead.email}</span>
                </div>
                <div className="flex items-center gap-2 text-text-secondary">
                  <FiMapPin className="flex-shrink-0" />
                  <span>{formattedLead.location}</span>
                </div>
              </div>
            </div>

            {/* Property details */}
            <div className="text-sm grid grid-cols-1 sm:grid-cols-2 md:grid-cols-8 gap-4 sm:gap-2 py-4 px-4 lg:px-0">
              <div className="col-span-1 md:col-span-2 flex gap-x-4">
                <div className="h-full border-[3px] rounded-full border-gray-300/50 hidden sm:block"></div>
                <div>
                  <p className="text-text-muted font-medium mb-1">Search For</p>
                  <p className="text-text-primary font-medium line-clamp-3">
                    {formattedLead.searchFor}
                  </p>
                </div>
              </div>
              <div className="col-span-1 sm:col-span-1 md:col-span-3 flex gap-x-4">
                <div className="h-full border-[3px] rounded-full border-gray-300/50 hidden sm:block"></div>
                <div>
                  <p className="text-text-muted font-medium mb-1">
                    At the location
                  </p>
                  <p className="text-text-primary font-medium line-clamp-1">
                    {formattedLead.propertyLocation}
                  </p>
                </div>
              </div>
              <div className="col-span-1 sm:col-span-2 md:col-span-3 flex gap-x-4">
                <div className="h-full border-[3px] rounded-full border-gray-300/50 hidden sm:block"></div>
                <div>
                  <p className="text-text-muted font-medium mb-1">
                    Budget In between
                  </p>
                  <p className="text-text-primary font-medium">
                    {formattedLead.budget}
                  </p>
                </div>
              </div>
            </div>

            {/* Property image • agent details • timestamp */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:flex-row items-start sm:items-center py-4 px-4 lg:px-0">
              <div className="col-span-2 flex gap-3 items-center">
                <div className="rounded-md overflow-hidden flex-shrink-0">
                  <img
                    src={formattedLead.property.image}
                    alt={formattedLead.property.name}
                    className="w-20 h-[4.5rem] sm:w-24 sm:h-[4.5rem] object-cover"
                  />
                </div>
                <div className="p-3 sm:p-2 bg-white">
                  <h4 className="font-medium line-clamp-1 text-text-primary cursor-pointer text-sm mb-1">
                    {formattedLead.property.name}
                  </h4>
                  <p className="text-xs text-text-muted">
                    {formattedLead.property.developer}
                  </p>
                </div>
              </div>

              {/* CENTER: AGENT DETAILS (Only if available) */}
              {formattedLead.agent?._id && (
                <div className="col-span-1 space-y-2 text-sm lg:border-l lg:px-4 sm:px-2">
                  <p className="font-medium text-text-primary mb-1">
                    Agent Details
                  </p>

                  <div className="flex items-center gap-2 text-text-secondary">
                    <FiUsers className="flex-shrink-0" />
                    <span className="break-all">
                      {formattedLead.agent.name}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-text-secondary">
                    <FiPhone className="flex-shrink-0" />
                    <span className="break-all">
                      {formattedLead.agent.phone}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-text-secondary">
                    <MdEmail className="flex-shrink-0" />
                    <span className="break-all truncate">
                      {formattedLead.agent.email}
                    </span>
                  </div>
                </div>
              )}

              {/* RIGHT: Timestamp */}
              <div className="col-span-1  self-end  p-2 sm:ml-auto">
                <p className="text-xs tracking-wide text-text-muted">
                  {formattedLead.date}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const handleExport = (filteredLeads) => {
  try {
    if (!Array.isArray(filteredLeads) || filteredLeads.length === 0) {
      showError("No leads to export");
      return;
    }
    const headers = [
      "Lead Name",
      "Phone",
      "Email",
      "Location",
      "Search For",
      "Property Location",
      "Budget",
      "Status",
      "Property Name",
      "Agent Name",
      "Created At",
    ];
    const rows = filteredLeads.map((l) => [
      l.leadName || "",
      l.phoneNumber || "",
      l.email || "",
      l.location || "",
      l.searchCriteria?.searchFor || "",
      l.searchCriteria?.propertyLocation || "",
      l.searchCriteria?.budget?.min && l.searchCriteria?.budget?.max
        ? `${formatPrice(l.searchCriteria.budget.min)} - ${formatPrice(
            l.searchCriteria.budget.max
          )}`
        : "",
      l.leadStatus || "",
      l.propertyDetails?.name || "",
      l.agent?.name || "",
      l.createdAt ? new Date(l.createdAt).toLocaleString() : "",
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `leads_export_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSuccess("Export started");
  } catch (err) {
    console.error("Export error:", err);
    showError("Export failed");
  }
};

const SuperAdminPropertiesLeads = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [stats, setStats] = useState(statsData);

  useEffect(() => {
    let mounted = true;
    const fetchLeads = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get("/leads/all");
        const fetchedLeads = response?.data?.data || [];
        if (!mounted) return;
        setLeads(fetchedLeads);
        setFilteredLeads(fetchedLeads);

        setStats((prev) =>
          prev.map((s) => {
            if (s.id === 1) return { ...s, value: String(fetchedLeads.length) };
            if (s.id === 2)
              return {
                ...s,
                value: String(
                  fetchedLeads.filter((l) => l.leadStatus === "converted")
                    .length
                ),
              };
            if (s.id === 3)
              return {
                ...s,
                value: String(
                  fetchedLeads.filter((l) => l.leadStatus !== "not_interested")
                    .length
                ),
              };
            if (s.id === 4)
              return {
                ...s,
                value: String(
                  fetchedLeads.filter((l) => l.leadStatus === "converted")
                    .length
                ),
              };
            return s;
          })
        );
      } catch (err) {
        console.error("Error fetching leads:", err);
        setError("Server error while fetching leads.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchLeads();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let filtered = [...leads];

    // Search
    if (searchTerm.trim() !== "") {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter((lead) => {
        return (
          lead.leadName?.toLowerCase().includes(lower) ||
          lead.email?.toLowerCase().includes(lower) ||
          lead.phoneNumber?.toLowerCase().includes(lower) ||
          lead.location?.toLowerCase().includes(lower) ||
          lead.searchCriteria?.propertyLocation
            ?.toLowerCase()
            .includes(lower) ||
          lead.searchCriteria?.searchFor?.toLowerCase().includes(lower)
        );
      });
    }

    // Month filter
    if (selectedMonth !== "All") {
      filtered = filtered.filter((lead) => {
        if (!lead.createdAt) return false;
        const leadMonth = new Date(lead.createdAt).getMonth();
        const selectedIndex = new Date(`${selectedMonth} 1, 2024`).getMonth();
        return leadMonth === selectedIndex;
      });
    }

    setFilteredLeads(filtered);
  }, [searchTerm, selectedMonth, selectedFilter, leads]);

  return (
    <div className="min-h-screen font-quicksand bg-background py-3 sm:py-4 md:py-6 pl-3 sm:pl-4 md:pl-6 pr-2 sm:pr-3 md:pr-4">
      {/* Header */}
      <div className="mb-4 cursor-default font-poppins">
        <h1 className="text-2xl font-semibold text-text-primary mb-1">
          Lead/ Responses
        </h1>
        <p className="text-text-muted text-sm">Lead/ Response</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ">
        {stats.map((s) => (
          <StatCard key={s.id} {...s} />
        ))}
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-9 ">
          <SearchFilterBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            onExport={handleExport}
          />

          <FilterChipsBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
          />

          {error && <p className="text-center text-red-500 py-4">{error}</p>}

          {!error && (
            <div className="space-y-4 scroll-overlay">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <LeadCardSkeleton key={i} />
                ))
              ) : Array.isArray(filteredLeads) && filteredLeads.length > 0 ? (
                filteredLeads.map((lead) => (
                  <LeadCard key={lead._id} lead={lead} />
                ))
              ) : leads.length === 0 ? (
                <NoLeadsScreen isFiltered={false} />
              ) : (
                <NoLeadsScreen
                  isFiltered={true}
                  onReset={() => {
                    setSearchTerm("");
                    setSelectedMonth("All");
                    setSelectedFilter("All");
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="xl:col-span-3 space-y-4 min-h-screen mt-4">
          <div className="xl:sticky xl:top-4 space-y-4 ">
            <SuperAdminPremiumSubscriptionCard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPropertiesLeads;

// import React, { useEffect, useRef, useState } from "react";
// import { Z_INDEX } from "../../utils/constants";
// import {
//   FiUsers,
//   FiDownload,
//   FiSearch,
//   FiPhone,
//   FiMapPin,
//   FiFilter,
//   FiUserPlus,
//   FiTrendingUp,
//   FiCheckCircle,
// } from "react-icons/fi";
// import { IoChevronDown } from "react-icons/io5";
// import { BsWhatsapp } from "react-icons/bs";
// import { MdEmail } from "react-icons/md";
// import img from "../../assets-super-admin/userImg.png";
// import SuperAdminPremiumSubscriptionCard from "../../components/super-admin/SuperAdminPremiumSubscriptionCard";

// const leadStatuses = [
//   { label: "New", color: "text-status-info" },
//   { label: "Contacted", color: "text-status-warning" },
//   { label: "Interested", color: "text-primary" },
//   { label: "Visit Scheduled", color: "text-status-success" },
//   { label: "Not Interested", color: "text-status-danger" },
//   { label: "Converted", color: "text-status-success" },
// ];

// // Stats Data
// const statsData = [
//   {
//     id: 1,
//     title: "No. of Leads",
//     value: "20",
//     link: "View all Leads",
//     percentage: "0.29%",
//     isPositive: true,
//     icon: FiUserPlus,
//     iconBg: "#735DFF",
//   },
//   {
//     id: 2,
//     title: "No. of Deals ",
//     value: "20",
//     link: "complete revenue",
//     percentage: "3.45%",
//     isPositive: false,
//     icon: FiUsers,
//     iconBg: "#FF5A29",
//   },
//   {
//     id: 3,
//     title: "Active Deals",
//     value: "20",
//     link: "Total page views",
//     percentage: "11.54%",
//     isPositive: true,
//     icon: FiTrendingUp,
//     iconBg: "#0CC763",
//   },
//   {
//     id: 4,
//     title: "Deal Closed",
//     value: "20",
//     link: "Total profit earned",
//     percentage: "0.18%",
//     isPositive: true,
//     icon: FiCheckCircle,
//     iconBg: "#0C9CFC",
//   },
// ];

// // Leads Data
// const leadsData = [
//   {
//     id: 1,
//     name: "Digbijay Sharma",
//     userId: "User-1",
//     phone: "+91 99874 56253",
//     email: "digbijaysharma23@gmail.com",
//     location: "Patia, Bhubaneswar",
//     searchFor: "3 BHK",
//     propertyLocation: "Kalinga Vihar, Bhubaneswar",
//     budget: "0.8 Cr to 1.2 Cr",
//     property: {
//       name: "LA CASA",
//       developer: "Covenants Developer",
//       image:
//         "https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=400&h=300&fit=crop",
//     },
//   },
//   {
//     id: 2,
//     name: "Ananya Singh",
//     userId: "User-2",
//     phone: "+91 99584 67321",
//     email: "ananya.singh24@gmail.com",
//     location: "Kharavel Nagar, Bhubaneswar",
//     searchFor: "2 BHK Flat",
//     propertyLocation: "Chandrasekharpur, Bhubaneswar",
//     budget: "₹70 Lakh to ₹95 Lakh",
//     property: {
//       name: "Skyline Heights",
//       developer: "BlueStone Constructions",
//       image:
//         "https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?w=400&h=300&fit=crop",
//     },
//   },
//   {
//     id: 3,
//     name: "Amit Pradhan",
//     userId: "User-3",
//     phone: "+91 98271 88554",
//     email: "amit.pradhan89@gmail.com",
//     location: "Bomikhal, Bhubaneswar",
//     searchFor: "Independent House",
//     propertyLocation: "Khandagiri, Bhubaneswar",
//     budget: "₹1.5 Cr to ₹2 Cr",
//     property: {
//       name: "Maple Residency",
//       developer: "GreenLeaf Estates",
//       image:
//         "https://images.unsplash.com/photo-1599423300746-b62533397364?w=400&h=300&fit=crop",
//     },
//   },
//   {
//     id: 4,
//     name: "Sneha Reddy",
//     userId: "User-4",
//     phone: "+91 90127 33562",
//     email: "sneha.reddy@gmail.com",
//     location: "Saheed Nagar, Bhubaneswar",
//     searchFor: "Luxury Villa",
//     propertyLocation: "Pahala, Bhubaneswar",
//     budget: "₹2.8 Cr to ₹3.5 Cr",
//     property: {
//       name: "Palm Grove Villas",
//       developer: "Elite Builders",
//       image:
//         "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=300&fit=crop",
//     },
//   },
//   {
//     id: 5,
//     name: "Manas Behera",
//     userId: "User-5",
//     phone: "+91 97564 22418",
//     email: "manas.behera01@gmail.com",
//     location: "Nayapalli, Bhubaneswar",
//     searchFor: "1 BHK Studio",
//     propertyLocation: "Patrapada, Bhubaneswar",
//     budget: "₹35 Lakh to ₹50 Lakh",
//     property: {
//       name: "Urban Edge",
//       developer: "Zenith Realty",
//       image:
//         "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&h=300&fit=crop",
//     },
//   },
// ];

// // Stat Card Component
// const StatCard = ({
//   title,
//   value,
//   link,
//   percentage,
//   isPositive,
//   icon: Icon,
//   iconBg,
// }) => (
//   <div className="bg-background-card rounded-lg p-4 sm:p-5 shadow-sm">
//     <div className="flex justify-between items-start mb-4">
//       <div>
//         <p className="text-text-primary font-medium text-sm mb-2">{title}</p>
//         <h3 className="text-2xl font-semibold text-text-primary">{value}</h3>
//       </div>
//       <div
//         className="w-12 h-12 rounded-full flex items-center justify-center"
//         style={{ backgroundColor: iconBg }}
//       >
//         <Icon className="text-white text-xl size-7" />
//       </div>
//     </div>
//     <div className="flex justify-between items-center">
//       <button className="text-text-muted capitalize leading-relaxed underline underline-offset-2 cursor-pointer text-sm hover:text-primary">
//         {link}
//       </button>
//       <span
//         className={`text-sm font-semibold ${
//           isPositive ? "text-status-success" : "text-status-danger"
//         }`}
//       >
//         {percentage}
//       </span>
//     </div>
//   </div>
// );
// // Search and Filter Bar Component
// const SearchFilterBar = () => {
//   const [selectedMonth, setSelectedMonth] = useState("Month");
//   const [selectedFilter, setSelectedFilter] = useState("Filter by Lead Status");

//   return (
//     <div
//       style={{ zIndex: Z_INDEX.sticky }}
//       className="sticky top-0 py-4 bg-background"
//     >
//       <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
//         {/* Search Bar */}
//         <div className="flex-1 relative min-w-0">
//           <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
//           <input
//             type="text"
//             placeholder="Search"
//             className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary"
//           />
//         </div>

//         {/* Filters Container */}
//         <div className="flex gap-3">
//           {/* Month Dropdown */}
//           <div className="relative flex-1 sm:flex-initial">
//             <select
//               value={selectedMonth}
//               onChange={(e) => setSelectedMonth(e.target.value)}
//               className="appearance-none w-full pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary cursor-pointer sm:min-w-[120px]"
//             >
//               <option>Month</option>
//               <option>January</option>
//               <option>February</option>
//               <option>March</option>
//               <option>April</option>
//               <option>May</option>
//               <option>June</option>
//             </select>
//             <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
//           </div>

//           {/* Filter Dropdown */}
//           <div className="relative flex-1 sm:flex-initial">
//             <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
//             <select
//               value={selectedFilter}
//               onChange={(e) => setSelectedFilter(e.target.value)}
//               className="appearance-none w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary cursor-pointer sm:min-w-[180px]"
//             >
//               <option>Filter by Lead Status</option>
//               <option>New Leads</option>
//               <option>In Progress</option>
//               <option>Closed</option>
//             </select>
//             <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
//           </div>

//           {/* Export Button */}
//           <button className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">
//             <FiDownload className="text-text-secondary" />
//             <span className="text-sm font-medium text-text-secondary">
//               Export
//             </span>
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// const LeadCard = ({ lead }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [selectedValue, setSelectedValue] = useState("");
//   const dropdownRef = useRef(null);
//   const handleSelect = (value, label) => {
//     setSelectedValue(label);
//     setIsOpen(false);
//   };
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);
//   return (
//     <div className="bg-white rounded-lg font-poppins shadow-sm border-2 border-gray-200 hover:shadow-md transition-shadow">
//       <div className="flex flex-col lg:flex-row gap-4">
//         <div className="flex flex-col sm:flex-row gap-3 flex-1">
//           {/* User Profile Section */}
//           <div className="flex-shrink-0 flex flex-col items-center p-4 sm:p-3">
//             <div className="w-20 h-20 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center text-white font-bold text-xl">
//               <img
//                 src={img}
//                 alt="user"
//                 className="w-full h-auto object-contain"
//               />
//             </div>
//             {/* Action Buttons */}
//             <div className="flex sm:flex-col gap-2 mt-4">
//               <button className="p-2.5 flex items-center justify-center size-11 rounded-full bg-status-success/10 hover:bg-status-success/20 transition-colors">
//                 <BsWhatsapp className="text-status-success w-5 h-5 sm:w-full sm:h-full" />
//               </button>
//               <button className="p-2.5 flex items-center justify-center size-11 rounded-full bg-status-info/10 hover:bg-status-info/20 transition-colors">
//                 <MdEmail className="text-status-info w-5 h-5 sm:w-full sm:h-full" />
//               </button>
//               <button className="p-2.5 flex items-center justify-center size-11 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors">
//                 <FiPhone className="text-primary w-5 h-5 sm:w-full sm:h-full" />
//               </button>
//             </div>
//           </div>

//           {/* Details */}
//           <div className="flex-1 flex-col divide-y divide-gray-200">
//             {/* User Details and Status */}
//             <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
//               <div className="lg:border-r py-4 px-4 lg:px-0 border-primary-navy/10">
//                 <h2 className="font-medium text-base sm:text-sm lg:text-base">
//                   {lead?.name}
//                 </h2>
//                 <p className="text-sm mt-2 text-text-secondary">
//                   {lead.userId}
//                 </p>
//               </div>
//               <div className="space-y-2 py-4 px-4 lg:px-0 text-sm">
//                 <div className="flex items-center gap-2 text-text-secondary">
//                   <FiPhone className="flex-shrink-0" />
//                   <span className="break-all sm:break-normal">
//                     {lead.phone}
//                   </span>
//                 </div>
//                 <div className="flex items-center gap-2 text-text-secondary">
//                   <MdEmail className="flex-shrink-0" />
//                   <span className="truncate">{lead.email}</span>
//                 </div>
//                 <div className="flex items-center gap-2 text-text-secondary">
//                   <FiMapPin className="flex-shrink-0" />
//                   <span>{lead.location}</span>
//                 </div>
//               </div>
//               {/* Update Status Button */}
//               <div className="flex w-full h-full items-end lg:items-end justify-start lg:justify-end p-4">
//                 <div className="relative w-full lg:w-auto" ref={dropdownRef}>
//                   {/* Trigger Button */}
//                   <button
//                     onClick={() => setIsOpen(!isOpen)}
//                     className="w-full lg:w-auto flex items-center justify-between gap-3 px-5 py-2.5 border-2 border-primary text-primary rounded-lg bg-white hover:bg-primary hover:text-white transition-all duration-200 font-medium text-sm whitespace-nowrap shadow-sm hover:shadow-md group"
//                   >
//                     <span>{selectedValue || "Update Lead Status"}</span>
//                     <IoChevronDown
//                       className={`transition-transform duration-200 ${
//                         isOpen ? "rotate-180" : ""
//                       }`}
//                     />
//                   </button>

//                   {/* Dropdown Menu */}
//                   {isOpen && (
//                     <div className="absolute top-full left-0 lg:left-auto lg:right-0 w-full lg:w-auto mt-2 bg-white border border-border rounded-lg shadow-xl overflow-hidden z-50 lg:min-w-[220px]">
//                       {leadStatuses.map((option, index) => (
//                         <button
//                           key={option.label}
//                           onClick={() =>
//                             handleSelect(option.label, option.label)
//                           }
//                           className={`w-full px-5 py-3 text-left text-sm font-medium transition-all duration-150 hover:bg-gray-50 ${
//                             index < leadStatuses.length - 1
//                               ? "border-b border-border"
//                               : ""
//                           } ${option.color || "text-primary"} hover:pl-6`}
//                         >
//                           <div className="flex items-center gap-2">
//                             <span className="w-2 h-2 rounded-full bg-current opacity-70"></span>
//                             {option.label}
//                           </div>
//                         </button>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>
//             {/* Property Details */}
//             <div className="text-sm grid grid-cols-1 sm:grid-cols-2 md:grid-cols-8 gap-4 sm:gap-2 py-4 px-4 lg:px-0">
//               {/* Search Criteria */}
//               <div className="col-span-1 md:col-span-2 flex gap-x-4">
//                 <div className="h-full border-[3px] rounded-full border-gray-300/50 hidden sm:block"></div>
//                 <div>
//                   <p className="text-text-muted font-medium mb-1">Search For</p>
//                   <p className="text-text-primary font-medium">
//                     {lead.searchFor}
//                   </p>
//                 </div>
//               </div>
//               <div className="col-span-1 sm:col-span-1 md:col-span-3 flex gap-x-4">
//                 <div className="h-full border-[3px] rounded-full border-gray-300/50 hidden sm:block"></div>
//                 <div>
//                   <p className="text-text-muted font-medium mb-1">
//                     At the location
//                   </p>
//                   <p className="text-text-primary font-medium">
//                     {lead.propertyLocation}
//                   </p>
//                 </div>
//               </div>
//               <div className="col-span-1 sm:col-span-2 md:col-span-3 flex gap-x-4">
//                 <div className="h-full border-[3px] rounded-full border-gray-300/50 hidden sm:block"></div>
//                 <div>
//                   <p className="text-text-muted font-medium mb-1">
//                     Budget In between
//                   </p>
//                   <p className="text-text-primary font-medium">{lead.budget}</p>
//                 </div>
//               </div>
//             </div>
//             {/* Property Image and Owner */}
//             <div className="flex gap-3 py-4 px-4 lg:px-0">
//               <div className="rounded-md overflow-hidden flex-shrink-0">
//                 <img
//                   src={lead.property.image}
//                   alt={lead.property.name}
//                   className="w-20 h-[4.5rem] sm:w-24 sm:h-[4.5rem] object-cover"
//                 />
//               </div>
//               <div className="p-3 sm:p-2 bg-white">
//                 <h4 className="font-medium text-text-primary text-sm mb-1">
//                   {lead.property.name}
//                 </h4>
//                 <p className="text-xs text-text-muted">
//                   {lead.property.developer}
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// // Main Component
// const SuperAdminPropertiesLeads = () => {
//   return (
//     <div className="min-h-screen font-quicksand bg-background py-3 sm:py-4 md:py-6 pl-3 sm:pl-4 md:pl-6 pr-2 sm:pr-3 md:pr-4">
//       {/* Header */}
//       <div className="mb-4 cursor-default font-poppins">
//         <h1 className="text-2xl font-semibold text-text-primary mb-1">
//           Lead/ Responses
//         </h1>
//         <p className="text-text-muted text-sm">Lead/ Response</p>
//       </div>

//       {/* Stats Grid */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ">
//         {statsData.map((stat) => (
//           <StatCard key={stat.id} {...stat} />
//         ))}
//       </div>

//       {/* Main Content Grid */}
//       <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
//         {/* Leads Section - Scrollable */}
//         <div className="xl:col-span-9 ">
//           <SearchFilterBar />
//           <div className="space-y-4 scroll-overlay ">
//             {leadsData.map((lead) => (
//               <LeadCard key={lead.id} lead={lead} />
//             ))}
//           </div>
//         </div>

//         {/* Sticky Right Sidebar */}
//         <div className="xl:col-span-3 space-y-4 min-h-screen mt-4">
//           <div className="xl:sticky xl:top-4 space-y-4 ">
//             <SuperAdminPremiumSubscriptionCard />
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SuperAdminPropertiesLeads;
